/**
 * useDrawerState Hook
 *
 * Centralized state management for the Task Drawer component.
 * Handles loading, editing states, unsaved changes detection,
 * and user relationship tracking.
 *
 * @example
 * const {
 *   isEditingAssignee,
 *   isFollowers,
 *   checkUnsavedComments,
 *   executeWithUnsavedCheck,
 * } = useDrawerState({ taskId, currentTask, currentUser, commentFormRef });
 */

import { useState, useCallback, useMemo } from 'react';

/**
 * Custom hook for managing drawer state
 * @param {Object} options - Hook options
 * @param {string} options.taskId - Current task ID
 * @param {Object} options.currentTask - Current task data
 * @param {Object} options.currentUser - Current user data
 * @param {Object} options.commentFormRef - Reference to comment form
 * @returns {Object} Drawer state and handlers
 */
export function useDrawerState({ taskId, currentTask, currentUser, commentFormRef }) {
  // Editing states
  const [isEditingAssignee, setIsEditingAssignee] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [historiesFetched, setHistoriesFetched] = useState(false);

  // Follower modal state
  const [showFollowerModal, setShowFollowerModal] = useState(false);
  const [selectedFollowerId, setSelectedFollowerId] = useState(null);

  // Unsaved changes modal state
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  // Computed user relationship states
  const isFollowers = useMemo(() => {
    return (
      currentTask?.followers &&
      currentUser &&
      currentTask.followers.some((f) => f.id === currentUser.id)
    );
  }, [currentTask, currentUser]);

  const isCollaborator = useMemo(() => {
    return (
      currentTask?.collaborators &&
      currentUser &&
      currentTask.collaborators.some((c) => c.id === currentUser.id)
    );
  }, [currentTask, currentUser]);

  const isCurrentUserAssignee = useMemo(() => {
    return currentTask?.assigneeUsers
      ? currentTask.assigneeUsers.some((user) => user.id === currentUser?.id)
      : false;
  }, [currentTask, currentUser]);

  const isCreatedBy = useMemo(() => {
    return currentUser?.id === currentTask?.createdBy?.id;
  }, [currentTask, currentUser]);

  // User level/permissions
  const level = useMemo(() => {
    return currentUser?.role?.org?.level;
  }, [currentUser]);

  /**
   * Check if there are unsaved comments in the form
   * @returns {boolean} Whether there are unsaved changes
   */
  const checkUnsavedComments = useCallback(() => {
    if (!commentFormRef?.current) return false;

    const commentText = commentFormRef.current.getFieldValue('comment');
    const attachedFiles = commentFormRef.current.getFieldValue('attachedFiles');
    const attachedImages = commentFormRef.current.getFieldValue('attachedImages');

    return (
      (commentText && commentText.trim().length > 0) ||
      (attachedFiles && attachedFiles.length > 0) ||
      (attachedImages && attachedImages.length > 0)
    );
  }, [commentFormRef]);

  /**
   * Execute an action with unsaved changes check
   * Shows confirmation modal if there are unsaved changes
   * @param {Function} action - Action to execute
   */
  const executeWithUnsavedCheck = useCallback(
    (action) => {
      if (checkUnsavedComments()) {
        setPendingAction(() => action);
        setShowUnsavedModal(true);
      } else {
        action();
      }
    },
    [checkUnsavedComments]
  );

  /**
   * Confirm and execute pending action
   */
  const handleConfirmUnsavedChanges = useCallback(async () => {
    setShowUnsavedModal(false);
    if (pendingAction) {
      await pendingAction();
      setPendingAction(null);
    }
  }, [pendingAction]);

  /**
   * Cancel pending action
   */
  const handleCancelUnsavedChanges = useCallback(() => {
    setShowUnsavedModal(false);
    setPendingAction(null);
  }, []);

  /**
   * Reset drawer state (called when drawer closes or task changes)
   */
  const resetState = useCallback(() => {
    setIsEditingAssignee(false);
    setHistoriesFetched(false);
    setActiveTab('1');
    setShowFollowerModal(false);
    setSelectedFollowerId(null);
    setShowUnsavedModal(false);
    setPendingAction(null);
  }, []);

  return {
    // Editing states
    isEditingAssignee,
    setIsEditingAssignee,
    activeTab,
    setActiveTab,
    historiesFetched,
    setHistoriesFetched,

    // Follower modal
    showFollowerModal,
    setShowFollowerModal,
    selectedFollowerId,
    setSelectedFollowerId,

    // Unsaved changes
    showUnsavedModal,
    checkUnsavedComments,
    executeWithUnsavedCheck,
    handleConfirmUnsavedChanges,
    handleCancelUnsavedChanges,

    // User relationships
    isFollowers,
    isCollaborator,
    isCurrentUserAssignee,
    isCreatedBy,
    level,

    // Actions
    resetState,
  };
}

export default useDrawerState;
